/**
 * Tiny modal primitive: backdrop or Esc closes, body gets
 * `overflow-hidden` while open (iOS scroll-under), data-swipe-consume
 * keeps a chart swipe inside the sheet from changing tabs. Not
 * `<dialog>`: its iOS Safari support and styling only landed in 18.x.
 * docs/customer-app.md > Front-end > Modal
 */
import { useEffect } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export function Modal({ open, onClose, title, children }: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-swipe-consume="true"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      // Backdrop tap closes; inner card click stops propagation.
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" />
      {/* Sheet - slides up on mobile, centred card on desktop. */}
      <div
        className="relative w-full sm:max-w-lg bg-card rounded-t-2xl sm:rounded-2xl shadow-card max-h-[90vh] overflow-y-auto p-4 animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            aria-label="Sluiten"
            onClick={onClose}
            className="w-8 h-8 rounded-full grid place-items-center text-ink-mute hover:text-ink hover:bg-card-soft"
          >
            <svg width={18} height={18} viewBox="0 0 24 24" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
